import {Button, Modal} from "react-bootstrap";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {LoginUser, User} from "../../../services/UsersService";
import {userState} from "../../users/user/UserSlice";
import {moveLoginUser} from "../../users/UsersSlice";
import {moveLoginUserModalState} from "./MoveLoginUserModalSlice";
import {close, findUser, selectRole, selectUser} from "./MoveLoginUserModalSlice";

// Record<LoginUser["role"], true> makes adding a role to the union a compile
// error here, keeping the selector's option list in sync with the type.
const USER_ROLE_SET: Record<LoginUser["role"], true> = {
  superAdmin: true,
  admin: true,
  billingManager: true,
  teamMember: true,
  guest: true
};
const USER_ROLES = Object.keys(USER_ROLE_SET) as ReadonlyArray<LoginUser["role"]>;

// Friendly labels matching the dashboard's Security & Access screen, so agents
// see "Account Owner" instead of the raw "superAdmin" key when picking a role.
const ROLE_LABELS: Record<LoginUser["role"], string> = {
  superAdmin: "Account Owner",
  admin: "Admin",
  billingManager: "Billing Manager",
  teamMember: "Team Member",
  guest: "Guest"
};

const MoveLoginUserModal = () => {
  const {isOpened, loginUser, users, selectedUser, selectedRole} = useAppSelector(moveLoginUserModalState);
  const {user: currentUser} = useAppSelector(userState);

  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(close());
  };

  const handleSearch = () => {
    const searchInput = document.querySelector("#moveLoginUserModalSearch") as HTMLInputElement;
    dispatch(findUser(searchInput.value));
  };

  const handleUserSelected = (user: User) => {
    dispatch(selectUser(user));
    return false;
  };

  const handleRoleSelected = (role: LoginUser["role"]) => {
    dispatch(selectRole(role));
  };

  const handeMove = () => {
    if (loginUser && selectedUser && selectedRole) {
      dispatch(moveLoginUser({loginUser, targetUser: selectedUser, role: selectedRole}));
    }
  };

  return (
    <>
      <Modal show={isOpened} onHide={handleClose}>
        <Modal.Header closeButton={true}>
          <Modal.Title>Move login user</Modal.Title>
        </Modal.Header>
        <Modal.Body className="container">
          <div className="row">
            <div className="col">Login user:</div>
          </div>
          <div className="row">
            <div className="col-2">id:</div>
            <div className="col font-weight-bold">{loginUser?.id}</div>
          </div>
          <div className="row">
            <div className="col-2">email:</div>
            <div className="col font-weight-bold">{loginUser?.email}</div>
          </div>
          <div className="row">
            <div className="col">Current account (company)</div>
          </div>
          <div className="row">
            <div className="col-2">id:</div>
            <div className="col font-weight-bold">{currentUser?.id}</div>
          </div>
          <div className="row">
            <div className="col-2">email:</div>
            <div className="col">
              <span className="font-weight-bold">{currentUser?.email}</span>
            </div>
          </div>
          {selectedUser && (
            <div className="row">
              <div className="col-2">Target:</div>
              <div className="row">
                <div className="col-2">id:</div>
                <div className="col font-weight-bold">{selectedUser.id}</div>
              </div>
              <div className="row">
                <div className="col-2">email:</div>
                <div className="col font-weight-bold">{selectedUser.email}</div>
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-4">
              <label htmlFor="moveLoginUserModalRole">Permission role</label>
            </div>
            <div className="col">
              <select
                id="moveLoginUserModalRole"
                className="form-control"
                value={selectedRole ?? ""}
                onChange={(event) => {
                  handleRoleSelected(event.target.value as LoginUser["role"]);
                }}
              >
                {USER_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <input id="moveLoginUserModalSearch" type="text" className="form-control" />
            </div>
            <div className="col-2">
              <Button variant="secondary" onClick={handleSearch}>
                Search
              </Button>
            </div>
          </div>
          <ul>
            {users.map((u) => (
              <li key={u.id}>
                <button
                  className="btn btn-link"
                  onClick={() => {
                    handleUserSelected(u);
                  }}
                >{`${u.id} ${u.adminEmail}`}</button>
              </li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button disabled={selectedUser === null} onClick={handeMove} className="btn btn-danger">
            Move login user
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MoveLoginUserModal;
